import { definePlugin } from '../../framework';
import type { FieldSuggestion, TransformState, TransformStep } from '../../types';
import { confirmation, fieldExpectation, sameIgnoringSpaces } from '../../utils/confirmation';

// How two parts of one value sit next to each other in a document: on one
// line, in a list, or on the next line.
const SEPARATORS = [' ', ', ', '\n'];

// How many other suggestions are tried as a second part, to keep the number
// of combinations small.
const MAX_PARTNERS = 10;

const isWrapped = (snippet: unknown): snippet is [unknown[]] =>
  Array.isArray(snippet) && Array.isArray(snippet[0]) && snippet[0].length === 1;

// A part goes into the concatenation as itself, not inside the [[…]] trim
// wrapper some snippets carry.
const asPart = (snippet: unknown) => (isWrapped(snippet) ? snippet[0][0] : snippet);

/** The editor's Concatenate block: the parts with literal separators between them. */
export const buildConcatSnippet = (first: unknown, separator: string, second: unknown) => ({
  '$utils.concat.selective': [asPart(first), separator, asPart(second)],
});

const hasSnippet = (s: FieldSuggestion) =>
  s.snippet !== undefined && s.snippet !== null && s.snippet !== '';

/**
 * Values made of this suggestion and another one for the same field — a
 * multi-line address, a value spread over two cells, a date and a time —
 * that something confirms: the value the plugin expected, or another
 * suggestion, ignoring how spaces and line breaks are laid out.
 */
export const composeCandidates = (
  state: TransformState,
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: ReturnType<typeof fieldExpectation>,
): TransformState[] => {
  if (typeof state.value !== 'string' || suggestion.iterator === true) return [];
  const first = state.value.trim();
  if (first === '') return [];
  // The separator that gives exactly the confirmed text wins; otherwise the
  // first one that matches when spaces and line breaks are ignored.
  const exact = confirmation(suggestion, others, field, first);
  const loose = confirmation(suggestion, others, field, first, sameIgnoringSpaces);

  const partners = others
    .filter((o) => o !== suggestion && o.iterator !== true && hasSnippet(o))
    .filter((o) => typeof o.value === 'string' && o.value.trim() !== '' && o.value.trim() !== first)
    .slice(0, MAX_PARTNERS);

  return partners.flatMap((partner) => {
    const join = (separator: string) => `${first}${separator}${(partner.value as string).trim()}`;
    const separator =
      SEPARATORS.find((s) => exact(join(s))) ?? SEPARATORS.find((s) => loose(join(s)));
    if (separator === undefined) return [];
    return [
      {
        snippet: buildConcatSnippet(state.snippet, separator, partner.snippet),
        value: join(separator),
      },
    ];
  });
};

export const composeStep: TransformStep = {
  name: 'concatenate',
  stage: 'compose',
  mode: 'add',
  apply: (state, { entry, suggestion, suggestions }) =>
    composeCandidates(state, suggestion, suggestions, fieldExpectation(entry)),
};

export const compose = definePlugin<{}>({
  name: 'Concatenate',
  version: '0.1.0',
  description:
    'Replaces the Concatenate transformer: joins two suggestions for a field when something confirms the joined value.',
  initialState: {},

  bootstrap: () => {},

  transformSteps: () => [composeStep],
});
